export const destinations = [
  {
    slug: "uk",
    name: "United Kingdom",
    short: "UK",
    city: "London",
    code: "GB",
    image: "/img/london-city.jpg",
    hero: "/img/hero-london.jpg",
    tagline: "Heritage, enterprise and a world-class education system.",
    intro:
      "From the financial corridors of the City to the research campuses of Oxford and Cambridge, the United Kingdom remains one of the most sought-after destinations for professionals, founders and families. We guide clients through a points-based system that rewards skill, talent and enterprise.",
    pathways: [
      { title: "Skilled Worker", text: "For professionals with a job offer from a Home Office-licensed sponsor in an eligible occupation, with a route to settlement over time." },
      { title: "Global Talent", text: "For recognised leaders and emerging leaders in research, arts and culture, and digital technology. No job offer required, subject to endorsement." },
      { title: "Innovator Founder", text: "For experienced entrepreneurs establishing an innovative, viable and scalable business, endorsed by an approved body." },
      { title: "Senior or Specialist Worker", text: "For employees of multinational organisations transferring to a UK branch on an intra-company basis." },
      { title: "Family & Partner Routes", text: "For spouses, partners, children and dependants joining a British citizen or settled person." },
      { title: "Standard Visitor", text: "For tourism, business meetings, family visits and short-term activities permitted under the visitor rules." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "permanent-residence", "post-visa-help"],
    benefits: [
      "Access to a globally respected education system for children",
      "A defined route to Indefinite Leave to Remain on qualifying routes",
      "A leading international centre for finance, law and technology",
      "The NHS and a stable, transparent legal framework",
    ],
    eligibility: [
      "A qualifying job offer, endorsement or business plan, depending on route",
      "English language ability at the level required for the route",
      "Evidence of maintenance funds where applicable",
      "A clean immigration and character history",
    ],
    faqs: [
      { q: "Can I bring my family on a UK work visa?", a: "Most UK work routes allow eligible partners and children to apply as dependants, subject to the rules of the specific route and evidence of the relationship." },
      { q: "Is there still a UK investor visa?", a: "The former Tier 1 Investor route closed to new applicants in 2022. Entrepreneurs may consider the Innovator Founder route, subject to endorsement and eligibility." },
      { q: "How long before I can settle permanently?", a: "Many qualifying routes lead to Indefinite Leave to Remain after a continuous residence period. The applicable period depends on the route and current rules." },
    ],
  },
  {
    slug: "australia",
    name: "Australia",
    short: "Australia",
    city: "Sydney",
    code: "AU",
    image: "/img/sydney.jpg",
    hero: "/img/sydney-opera.jpg",
    tagline: "An outdoor lifestyle paired with a dynamic, skills-led economy.",
    intro:
      "Australia combines exceptional quality of life with a mature, skills-driven migration programme. Whether you are a sought-after professional, an employer-sponsored specialist or a family seeking a fresh horizon, we design a pathway around your profile and long-term aspirations.",
    pathways: [
      { title: "Skilled Independent", text: "A points-tested permanent visa for skilled workers who are not sponsored by an employer, state or family member." },
      { title: "Skilled Nominated", text: "A permanent visa for skilled workers nominated by an Australian state or territory government." },
      { title: "Skilled Work Regional", text: "A provisional visa for skilled workers who wish to live and work in designated regional Australia." },
      { title: "Employer Sponsored", text: "Temporary and permanent routes for professionals sponsored by an approved Australian employer." },
      { title: "National Innovation", text: "For individuals with an internationally recognised record of exceptional achievement in an eligible field." },
      { title: "Visitor", text: "For tourism, business visitor activities and visiting family in Australia." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "permanent-residence", "post-visa-help"],
    benefits: [
      "Permanent residence pathways for skilled professionals",
      "Access to Medicare and high-quality public education",
      "Strong, stable economy with in-demand occupations",
      "A route to citizenship after meeting residence requirements",
    ],
    eligibility: [
      "An occupation on the relevant skilled occupation list",
      "A positive skills assessment from the designated authority",
      "Competent English or higher, depending on visa",
      "Meeting the points threshold and age requirements where applicable",
    ],
    faqs: [
      { q: "What is a skills assessment?", a: "A skills assessment confirms that your qualifications and experience meet Australian standards for your nominated occupation. It is completed by a designated assessing authority." },
      { q: "Does meeting the points minimum guarantee an invitation?", a: "No. Invitations depend on the competitiveness of the pool and programme settings at the time. A higher score generally improves your prospects." },
      { q: "Can my partner's skills add points?", a: "In some cases, a partner's skills or English ability may contribute points, subject to the current points test." },
    ],
  },
  {
    slug: "new-zealand",
    name: "New Zealand",
    short: "New Zealand",
    city: "Auckland",
    code: "NZ",
    image: "/img/auckland-2.jpg",
    hero: "/img/auckland.jpg",
    tagline: "Unhurried living, progressive society and natural grandeur.",
    intro:
      "New Zealand offers a rare balance of safety, space and opportunity. Its residence system favours skilled professionals, accredited-employer hires and investors who wish to contribute to the country's growth. We help clients navigate each step with precision and care.",
    pathways: [
      { title: "Skilled Migrant Category", text: "A points-based resident visa for skilled workers with qualifications, registration or income meeting set thresholds." },
      { title: "Green List", text: "Straight-to-residence and work-to-residence pathways for roles on New Zealand's Green List." },
      { title: "Accredited Employer Work Visa", text: "A temporary work visa for those offered a job by an accredited New Zealand employer." },
      { title: "Active Investor Plus", text: "A residence route for investors making qualifying investments into New Zealand, subject to current programme settings." },
      { title: "Partner & Family", text: "Visas for partners and dependent children of New Zealand citizens and residents." },
      { title: "Visitor", text: "For holidays, visiting family and limited business visitor activities." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "permanent-residence", "pr-by-investment", "post-visa-help"],
    benefits: [
      "Clear residence pathways for in-demand professions",
      "An investor route for globally mobile families",
      "Exceptional safety and quality of life",
      "Access to public healthcare and education once eligible",
    ],
    eligibility: [
      "A qualifying job offer, occupation or investment, depending on route",
      "Health and character requirements",
      "English language ability where required",
      "Evidence supporting skills, income or investment funds",
    ],
    faqs: [
      { q: "What does 'accredited employer' mean?", a: "Employers must be accredited by Immigration New Zealand before they can support most migrant work visas. We verify accreditation status as part of our assessment." },
      { q: "Is the investor route still open?", a: "New Zealand's investor settings are periodically revised. We confirm the current programme rules with official sources before advising any client." },
      { q: "Can I move straight to residence?", a: "Certain Green List roles offer a straight-to-residence pathway, subject to meeting all requirements for that role." },
    ],
  },
  {
    slug: "canada",
    name: "Canada",
    short: "Canada",
    city: "Toronto",
    code: "CA",
    image: "/img/toronto-2.jpg",
    hero: "/img/toronto.jpg",
    tagline: "Opportunity, inclusion and a defined path to permanence.",
    intro:
      "Canada's economic immigration system is among the most structured in the world. From Express Entry to provincial nomination and entrepreneurial streams, we build strategies that position each client's profile competitively and compliantly.",
    pathways: [
      { title: "Express Entry", text: "The federal system managing the Federal Skilled Worker, Canadian Experience Class and Federal Skilled Trades programmes." },
      { title: "Provincial Nominee Programs", text: "Province-specific streams targeting skills, occupations and entrepreneurs aligned with local labour needs." },
      { title: "Start-up Visa", text: "For founders with an innovative business supported by a designated Canadian organisation." },
      { title: "LMIA & Employer Work Permits", text: "Employer-supported work permits, including intra-company transfers for multinational staff." },
      { title: "Family Sponsorship", text: "For spouses, partners, dependent children and, in some cases, parents and grandparents." },
      { title: "Visitor Visa", text: "For tourism, visiting family and short business trips to Canada." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "permanent-residence", "pr-by-investment", "post-visa-help"],
    benefits: [
      "A transparent, points-based permanent residence system",
      "Universal public healthcare and excellent schools",
      "Spousal open work permits on many routes",
      "A recognised path to Canadian citizenship",
    ],
    eligibility: [
      "Skilled work experience in an eligible occupation",
      "Approved language test results in English or French",
      "An Educational Credential Assessment for foreign qualifications",
      "Proof of settlement funds where required",
    ],
    faqs: [
      { q: "What is the CRS score?", a: "The Comprehensive Ranking System scores Express Entry profiles on factors such as age, education, language and experience. Higher-ranked candidates may receive Invitations to Apply." },
      { q: "Do I need a job offer for Canada PR?", a: "Not always. Many candidates are invited through Express Entry without a job offer, though an offer or provincial nomination may strengthen a profile." },
      { q: "Can I include my family?", a: "Yes, eligible spouses, partners and dependent children can generally be included in a permanent residence application." },
    ],
  },
  {
    slug: "usa",
    name: "United States",
    short: "USA",
    city: "New York",
    code: "US",
    image: "/img/nyc-empire.jpg",
    hero: "/img/newyork.jpg",
    tagline: "The world's largest economy and a magnet for ambition.",
    intro:
      "The United States offers unmatched scale for professionals, investors and exceptional talent. Its immigration framework is intricate, and success depends on choosing the right category from the outset. We provide structured guidance and coordinate closely with licensed US counsel where required.",
    pathways: [
      { title: "EB-5 Immigrant Investor", text: "A green card route for investors making a qualifying investment in a US commercial enterprise that creates jobs." },
      { title: "EB-1 & EB-2 NIW", text: "Employment-based green cards for individuals of extraordinary ability or whose work is in the US national interest." },
      { title: "O-1 Extraordinary Ability", text: "A temporary visa for individuals with sustained national or international acclaim in their field." },
      { title: "L-1 Intra-company Transfer", text: "For managers, executives and specialised staff transferring to a US affiliate of their employer." },
      { title: "H-1B Specialty Occupation", text: "For professionals in specialty occupations sponsored by a US employer, subject to the annual cap process." },
      { title: "B-1/B-2 Visitor", text: "For business meetings, tourism and visiting family in the United States." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "permanent-residence", "pr-by-investment", "post-visa-help"],
    benefits: [
      "Access to the world's deepest capital and talent markets",
      "Investor and talent-based green card routes",
      "World-leading universities and research institutions",
      "Dual-intent options on certain temporary visas",
    ],
    eligibility: [
      "A qualifying employer, investment or record of achievement",
      "Supporting evidence tailored to the selected category",
      "Lawful source of funds for investor categories",
      "Admissibility under US immigration law",
    ],
    faqs: [
      { q: "Do you provide US legal representation?", a: "Where a matter requires a licensed US attorney, we coordinate with trusted immigration counsel while managing your preparation, documentation and timeline end to end." },
      { q: "Is the EB-5 investment amount fixed?", a: "Qualifying investment amounts are set by US law and may change. We confirm current requirements with official sources during your consultation." },
      { q: "What is the H-1B lottery?", a: "H-1B visas are subject to an annual numerical cap. When registrations exceed available numbers, selection is made through a random process." },
    ],
  },
  {
    slug: "european-union",
    name: "European Union",
    short: "European Union",
    city: "Paris",
    code: "EU",
    image: "/img/paris-2.jpg",
    hero: "/img/paris.jpg",
    tagline: "Twenty-seven nations, one remarkable gateway to mobility.",
    intro:
      "Europe offers an extraordinary choice of lifestyles, from Mediterranean coastlines to dynamic Northern capitals. Each member state sets its own residence rules, and programmes evolve frequently. We help families compare options thoughtfully and pursue the route that best fits their goals.",
    pathways: [
      { title: "EU Blue Card", text: "A residence and work permit for highly qualified non-EU professionals with a qualifying job offer." },
      { title: "Residence by Investment", text: "Investor residence programmes offered by selected member states, subject to current national rules." },
      { title: "Entrepreneur & Start-up", text: "Residence routes for founders establishing innovative businesses in a member state." },
      { title: "Financially Independent Residence", text: "For individuals with stable passive income who wish to reside without working locally." },
      { title: "Digital Nomad Permits", text: "For remote professionals employed or contracted outside the host country, where available." },
      { title: "Schengen Visitor", text: "Short-stay visas for tourism, business and family visits across the Schengen Area." },
    ],
    services: ["visitor-visa", "pre-visa-help", "work-permits", "pr-by-investment", "citizenship-by-investment", "post-visa-help"],
    benefits: [
      "Freedom of travel within the Schengen Area",
      "A diverse choice of lifestyles, climates and cultures",
      "Pathways to long-term residence and, in time, citizenship",
      "Access to world-class healthcare and education",
    ],
    eligibility: [
      "A qualifying job offer, investment, business or passive income",
      "Comprehensive health insurance",
      "Clean criminal record certificates",
      "Proof of accommodation in the host country",
    ],
    faqs: [
      { q: "Does residence in one EU country let me live in another?", a: "Generally no. A residence permit grants rights in the issuing country and short-stay travel within Schengen. Moving to another member state usually requires its own permit." },
      { q: "Are Golden Visa programmes still available?", a: "Several programmes have been reformed or closed in recent years. We verify each programme's current status before advising." },
      { q: "Can residence lead to citizenship?", a: "In many member states, long-term lawful residence can lead to citizenship, subject to residence periods, language and integration requirements." },
    ],
    euCountries: [
      { name: "Portugal", text: "Investment-fund and cultural routes under the reformed Golden Visa, the D7 passive income visa, the D8 digital nomad visa and the Tech Visa for skilled professionals." },
      { name: "Spain", text: "The non-lucrative residence visa, digital nomad visa and highly qualified professional permits. Spain's investor residence route closed in 2025." },
      { name: "Italy", text: "The Investor Visa for Italy, elective residence for financially independent individuals, and EU Blue Card for skilled hires." },
      { name: "Malta", text: "The Malta Permanent Residence Programme, the Global Residence Programme and the Nomad Residence Permit, subject to current criteria." },
      { name: "Greece", text: "The Golden Visa through qualifying real estate or financial investments, alongside the digital nomad visa and financially independent person permit." },
      { name: "Ireland", text: "The Critical Skills Employment Permit and Stamp 0 for persons of independent means. The Immigrant Investor Programme closed in 2023." },
      { name: "Germany", text: "The EU Blue Card, skilled worker permits and the Opportunity Card (Chancenkarte) for job seekers with qualifying credentials." },
      { name: "France", text: "The Talent Passport family of permits for investors, founders, highly qualified employees and individuals of international renown." },
      { name: "Netherlands", text: "The Highly Skilled Migrant scheme, the start-up visa and the Dutch-American Friendship Treaty for eligible nationals." },
    ],
  },
];

export const getDestination = (slug) => destinations.find((d) => d.slug === slug);
